import { useState, useEffect } from 'react'
import { Eye, ArrowLeft, Users, Trophy, MessageCircle, Send, X } from 'lucide-react'
import { subscribeToRoom, subscribeToChat, sendChatMessage, getSpectatorData } from '../utils/multiplayerAPI.js'
import './SpectatorMode.css'

export default function SpectatorMode({ roomId, players, onLeave }) {
  const [chatMessages, setChatMessages] = useState([])
  const [newMessage, setNewMessage] = useState('')
  const [playerList, setPlayerList] = useState(players || [])
  const [showChat, setShowChat] = useState(false)
  const [spectatorCount, setSpectatorCount] = useState(0)
  
  useEffect(() => {
    // Fetch initial data
    const fetchData = async () => {
      try {
        const data = await getSpectatorData(roomId)
        setPlayerList(data.players)
        setSpectatorCount(data.spectators.length)
      } catch (err) {
        console.error('Error fetching spectator data:', err)
      }
    }
    fetchData()
    
    // Subscribe to room updates
    const roomSubscription = subscribeToRoom(roomId, (payload) => {
      const { eventType, new: newData } = payload
      if (eventType === 'UPDATE') {
        setPlayerList(prev => prev.map(p => p.id === newData.id ? newData : p))
      }
    })
    
    // Subscribe to chat
    const chatSubscription = subscribeToChat(roomId, (message) => {
      setChatMessages(prev => [...prev, message])
    })
    
    return () => {
      roomSubscription.unsubscribe()
      chatSubscription.unsubscribe()
    }
  }, [roomId, players])
  
  const handleSendMessage = async () => {
    if (!newMessage.trim()) return
    
    // Get player info from localStorage
    const playerName = localStorage.getItem('playerName') || 'Spectator'
    const playerId = localStorage.getItem('playerId') || 'unknown'
    
    await sendChatMessage(roomId, playerId, playerName, newMessage)
    setNewMessage('')
  }
  
  const sortedPlayers = [...playerList].sort((a, b) => (b.score || 0) - (a.score || 0))
  
  return (
    <main className="spectator-mode">
      {/* Header */}
      <header className="spectator-header">
        <button className="back-btn" onClick={onLeave}>
          <ArrowLeft size={20} />
          Leave
        </button>
        
        <div className="spectator-title">
          <Eye size={24} />
          <span>Spectator Mode</span>
        </div>
        
        <div className="spectator-stats">
          <Users size={18} />
          <span>{playerList.length} Players</span>
          <span className="divider">|</span>
          <Eye size={18} />
          <span>{spectatorCount} Spectators</span>
        </div>
      </header>
      
      {/* Main Content */}
      <div className="spectator-content">
        {/* Live Leaderboard Panel */}
        <section className="leaderboard-panel">
          <h2>
            <Trophy size={24} />
            Live Leaderboard
          </h2>
          
          <div className="live-players">
            {sortedPlayers.map((player, index) => (
              <div key={player.id} className={`live-player rank-${index + 1}`}>
                <div className="player-rank-badge">#{index + 1}</div>
                <div className="player-info">
                  <span className="player-name">{player.name}</span>
                  <div className="player-progress">
                    <div className="progress-bar">
                      <div 
                        className="progress-fill" 
                        style={{ width: `${Math.min(((player.stage + 1) / 4) * 100, 100)}%` }}
                      />
                    </div>
                    <span>Stage {player.stage + 1}/4</span>
                  </div>
                </div>
                <div className="player-live-score">
                  {(player.score || 0).toLocaleString()}
                </div>
              </div>
            ))}
          </div>
          
          {sortedPlayers.length === 0 && (
            <div className="waiting-message">
              <p>Waiting for players to start...</p>
            </div>
          )}
        </section>
        
        {/* Player Activity Feed */}
        <section className="activity-panel">
          <h3>Live Activity</h3>
          <div className="activity-feed">
            {playerList.map(player => (
              <div key={player.id} className="activity-item">
                <div className="activity-avatar">{player.name[0]}</div>
                <div className="activity-details">
                  <span className="activity-player">{player.name}</span>
                  <span className="activity-action">
                    {player.stage === 3 ? 'Completed all stages!' : `On Stage ${player.stage + 1}`}
                  </span>
                </div>
                <span className="activity-time">Just now</span>
              </div>
            ))}
          </div>
        </section>
      </div>
      
      {/* Chat Toggle Button */}
      <button 
        className="chat-toggle"
        onClick={() => setShowChat(!showChat)}
      >
        <MessageCircle size={24} />
        {chatMessages.length > 0 && (
          <span className="chat-badge">{chatMessages.length}</span>
        )}
      </button>
      
      {/* Chat Panel */}
      {showChat && (
        <div className="chat-panel">
          <div className="chat-header">
            <h4>Game Chat</h4>
            <button className="close-chat" onClick={() => setShowChat(false)}>
              <X size={20} />
            </button>
          </div>
          
          <div className="chat-messages">
            {chatMessages.length === 0 ? (
              <p className="no-messages">No messages yet. Start the conversation!</p>
            ) : (
              chatMessages.map((msg, index) => (
                <div key={index} className="chat-message">
                  <span className="chat-author">{msg.player_name}</span>
                  <span className="chat-text">{msg.message}</span>
                </div>
              ))
            )}
          </div>
          
          <div className="chat-input-area">
            <input
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="Type a message..."
              className="chat-input"
              onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
            />
            <button className="send-btn" onClick={handleSendMessage}>
              <Send size={18} />
            </button>
          </div>
        </div>
      )}
      
      {/* Footer */}
      <footer className="spectator-footer">
        <p>You're watching in spectator mode. Scores update in real-time.</p>
      </footer>
    </main>
  )
}
